"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { paginate, type components, type Page } from "@theta/api-client";
import { thetaApi, dataOf, pageOf, MAX_PAGE_LIMIT } from "@/lib/api/theta";
import { retryUnlessRefused } from "./queryRetry";

/**
 * EVENTS, ON /api/v1 — spec §2.8
 *
 * `lib/api/events.ts` is gone, and with it SIX read functions that were one
 * collection wearing four hats: `getEvents`, `getPublicEvents`,
 * `getGroupEvents`, `getMonthEvents`, `getPublicMonthEvents` and `getAttendees`
 * are `GET /events` with `?group=`, `?from=`, `?to=`, `?past=` — plus
 * `/events/{id}/rsvps`, which is what `/attendees` is called now.
 *
 * `?year=&month=` is gone in favour of an ISO WINDOW, so the calendar asks for
 * exactly the month it draws and a week view costs nothing. `?past=` still
 * walks backwards in time and composes with the window rather than overriding
 * it.
 *
 * THREE THINGS THE CONTRACT NOW DECIDES THAT THE SCREEN USED TO GUESS:
 *  - `can.edit` / `can.delete` / `can.rsvp` / `can.comment`, from the
 *    predicates the routes enforce. `is_creator` is gone (D11) because
 *    recomputing "may I edit this" from "did I make it" was already wrong for
 *    a group admin.
 *  - CAPACITY. A `going` RSVP on a full event is `409 eventAtCapacity`; the
 *    legacy route let it through and the number went past the cap.
 *  - RSVPing REQUIRES AN ACTIVE MEMBERSHIP of a hosting group. Seeing an event
 *    is no longer the same as being able to answer it.
 *
 * The `"remove"` pseudo-status is deleted from the vocabulary: taking an RSVP
 * back is `DELETE /events/{id}/rsvp`, which answers 200 with the new counts
 * even when there was nothing to remove.
 */

type Schemas = components["schemas"];

export type ThetaEvent = Schemas["Event"];
type Rsvp = Schemas["Rsvp"];
type RsvpState = Schemas["RsvpState"];
export type RsvpStatus = Schemas["SetRsvpBodyInput"]["status"];
export type EventPostCard = Schemas["EventPostCard"];
export type CreateEventInput = Schemas["CreateEventBodyInput"];
export type UpdateEventInput = Schemas["UpdateEventBodyInput"];

interface EventFilters {
  /** A group id or slug (D8) — a group the viewer may not read is a 404. */
  group?: string | number | undefined;
  /** ISO instant, inclusive. */
  from?: string | undefined;
  /** ISO instant, exclusive. */
  to?: string | undefined;
  /** Walk backwards in time. Composes with the window; it does not override it. */
  past?: boolean | undefined;
  limit?: number | undefined;
}

function eventsKey(filters: EventFilters): string[] {
  return [
    "v1",
    "events",
    filters.group === undefined ? "" : String(filters.group),
    filters.from ?? "",
    filters.to ?? "",
    String(filters.past ?? false),
  ];
}

export function useV1Events(filters: EventFilters = {}, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: eventsKey(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/events", {
          params: {
            query: {
              ...(filters.group !== undefined ? { group: String(filters.group) } : {}),
              ...(filters.from ? { from: filters.from } : {}),
              ...(filters.to ? { to: filters.to } : {}),
              ...(filters.past ? { past: "true" as const } : {}),
              ...(filters.limit ? { limit: filters.limit } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        })
        .then(pageOf<ThetaEvent>),
    getNextPageParam: (last: Page<ThetaEvent>) => (last.hasMore ? last.nextCursor : undefined),
    enabled: options.enabled ?? true,
    retry: retryUnlessRefused,
  });
}

export function flatEvents(data: { pages: Page<ThetaEvent>[] } | undefined): ThetaEvent[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * A MONTH, WHOLE — the calendar cannot render half a window
 *
 * The calendar asked `useV1Events({...monthWindow, limit: 100})` and never
 * called `fetchNextPage`, so a busy month drew only its first page: days late
 * in the month came out EMPTY and the grid looked entirely correct while being
 * wrong. `MAX_LIMIT` is 100 (http/cursor.ts) so the limit cannot simply be
 * raised — and a partially-loaded calendar is not a short list, it is a false
 * one, which is why this walks the cursors instead of offering a "load more"
 * the way a list does.
 *
 * `paginate` keeps the cursor inside itself, so a filter change cannot feed a
 * cursor minted under another window back to the server (`400 cursorMismatch`,
 * §1.3).
 */
export function useV1EventWindow(
  window: { from: string; to: string },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...eventsKey(window), "all"],
    queryFn: async () => {
      const events: ThetaEvent[] = [];
      for await (const event of paginate<ThetaEvent>((cursor) =>
        thetaApi.http
          .GET("/events", {
            params: {
              query: {
                from: window.from,
                to: window.to,
                limit: MAX_PAGE_LIMIT,
                ...(cursor ? { cursor } : {}),
              },
            },
          })
          .then(pageOf<ThetaEvent>),
      )) {
        events.push(event);
      }
      return events;
    },
    enabled: options.enabled ?? true,
    retry: retryUnlessRefused,
  });
}

/**
 * One event.
 *
 * A soft-deleted event is a 404 and not a 410 (E7), byte-identical to one that
 * never existed — and so is an event this viewer may not see. The screen
 * renders both as "findes ikke".
 */
export function useV1Event(id: number) {
  return useQuery({
    queryKey: ["v1", "event", id],
    queryFn: () =>
      thetaApi.http.GET("/events/{id}", { params: { path: { id } } }).then(dataOf<ThetaEvent>),
    enabled: id > 0,
    retry: retryUnlessRefused,
  });
}

/**
 * Who answered, oldest first. `/attendees` renamed; the offset pager and its
 * `meta.total` are gone (E10). Signed-in only since OSO-695 — the caller
 * gates `enabled` on `useSignedIn()`, because the 401 a signed-out ask would
 * get is already known.
 */
export function useV1EventRsvps(
  id: number,
  status?: RsvpStatus,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: ["v1", "event-rsvps", id, status ?? ""],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/events/{id}/rsvps", {
          params: {
            path: { id },
            query: {
              ...(status ? { status } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        })
        .then(pageOf<Rsvp>),
    getNextPageParam: (last: Page<Rsvp>) => (last.hasMore ? last.nextCursor : undefined),
    enabled: id > 0 && (options.enabled ?? true),
    retry: retryUnlessRefused,
  });
}

/**
 * The event's discussion. `/comments` renamed to `/posts`, and each row is the
 * SAME card the group feed serves — `topicId` is gone entirely rather than
 * shipped as a hard-coded `null` (E8), because there are no topics.
 *
 * Cursor-paged, oldest first; the legacy read returned the whole discussion
 * unpaged.
 */
export function useV1EventPosts(id: number) {
  return useInfiniteQuery({
    queryKey: ["v1", "event-posts", id],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/events/{id}/posts", {
          params: { path: { id }, query: pageParam ? { cursor: pageParam } : {} },
        })
        .then(pageOf<EventPostCard>),
    getNextPageParam: (last: Page<EventPostCard>) => (last.hasMore ? last.nextCursor : undefined),
    enabled: id > 0,
    retry: retryUnlessRefused,
  });
}

function invalidateEvents(queryClient: QueryClient, id?: number) {
  void queryClient.invalidateQueries({ queryKey: ["v1", "events"] });
  if (id !== undefined) {
    void queryClient.invalidateQueries({ queryKey: ["v1", "event", id] });
  }
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventInput) =>
      thetaApi.http.POST("/events", { body }).then(dataOf<ThetaEvent>),
    onSuccess: (event) => invalidateEvents(queryClient, event.id),
  });
}

/** PUT became PATCH (§1.7): an absent key is untouched, an explicit `null` clears. */
export function useUpdateEvent(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEventInput) =>
      thetaApi.http
        .PATCH("/events/{id}", { params: { path: { id } }, body })
        .then(dataOf<ThetaEvent>),
    onSuccess: () => invalidateEvents(queryClient, id),
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      thetaApi.http.DELETE("/events/{id}", { params: { path: { id } } }).then(dataOf<null>),
    onSuccess: (_data, id) => invalidateEvents(queryClient, id),
  });
}

/**
 * Set, swap or withdraw the viewer's RSVP.
 *
 * The response is the COMPLETE, AUTHORITATIVE summary — splice it into the
 * event already in the cache instead of recomputing the transition, which is
 * what the old hook did by hand and got wrong the moment capacity started
 * being enforced (a refused `going` used to leave the optimistic count up).
 * `null` withdraws.
 */
export function useV1Rsvp(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: RsvpStatus | null) =>
      status === null
        ? thetaApi.http
            .DELETE("/events/{id}/rsvp", { params: { path: { id } } })
            .then(dataOf<RsvpState>)
        : thetaApi.http
            .PUT("/events/{id}/rsvp", { params: { path: { id } }, body: { status } })
            .then(dataOf<RsvpState>),
    onSuccess: (state) => {
      queryClient.setQueryData<ThetaEvent>(["v1", "event", id], (old) =>
        old ? { ...old, rsvpSummary: state.rsvpSummary, myRsvp: state.myRsvp } : old,
      );
      void queryClient.invalidateQueries({ queryKey: ["v1", "events"] });
      void queryClient.invalidateQueries({ queryKey: ["v1", "event-rsvps", id] });
    },
  });
}

/** Comment on an event. Requires `can.comment`; a file additionally requires `can.commentWithAttachment`. */
export function useCreateEventPost(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["CreateEventPostBodyInput"]) =>
      thetaApi.http
        .POST("/events/{id}/posts", { params: { path: { id } }, body })
        .then(dataOf<EventPostCard>),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["v1", "event-posts", id] });
      void queryClient.invalidateQueries({ queryKey: ["v1", "event", id] });
    },
  });
}
